import { todayKeyInZone } from './matchesDay';
import type { DrawResult } from '@/draw/types';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { isCompletedGame } from '@/app/[locale]/match/libs/retirement';
import type { PublicMatch } from '../[slug]/(tabs)/schedule/libs/scheduleStatus';
import type { MatchRowVM } from './eventMatchesView';
import type { EventLiveScore } from './liveScores';

/** Compact summaries never replace participant identity, ratings, filters or layout. */
function scoreGames(score: EventLiveScore) {
  const games = new Map(score.games.map((game) => [game.game_number, game]));
  if (score.status === 'in_progress' && score.current_game)
    games.set(score.current_game.game_number, score.current_game);
  return [...games.values()].sort((a, b) => a.game_number - b.game_number);
}
const isRetiredScore = (score: EventLiveScore) =>
  score.outcome === 'retired' || !!score.retired_player_id;
function tally(score: EventLiveScore) {
  return score.player_1_games === null || score.player_2_games === null
    ? null
    : { p1: score.player_1_games, p2: score.player_2_games };
}

export function applyMatchRowScore(
  row: MatchRowVM,
  score: EventLiveScore | undefined,
  players: [string | null, string | null],
): MatchRowVM {
  if (!score || row.id !== score.schedule_id) return row;
  const winnerless = isWinnerlessRow({ outcome: score.outcome, is_walkover: score.is_walkover });
  const state = winnerless
    ? 'winnerless'
    : score.status === 'in_progress'
      ? 'live'
      : score.status === 'completed'
        ? 'finished'
        : 'upcoming';
  const games = scoreGames(score).map((game) => ({
    p1: game.player_1_score,
    p2: game.player_2_score,
    ...(isCompletedGame(game) ? {} : { partial: true }),
  }));
  return {
    ...row,
    state,
    retired: isRetiredScore(score),
    matchId: score.match_id,
    outcome: score.outcome,
    isWalkover: score.is_walkover,
    games,
    gamesWon: tally(score),
    sides: [
      {
        ...row.sides[0],
        won: state === 'finished' && !!score.winner_id && score.winner_id === players[0],
      },
      {
        ...row.sides[1],
        won: state === 'finished' && !!score.winner_id && score.winner_id === players[1],
      },
    ],
    hasScorecard:
      !!score.match_id && (state === 'live' || (state === 'finished' && games.length > 0)),
  };
}

export function applyScheduleScore(
  match: PublicMatch,
  score: EventLiveScore | undefined,
): PublicMatch {
  if (!score || match.id !== score.schedule_id) return match;
  const status =
    score.status === 'in_progress'
      ? 'live'
      : score.status === 'completed'
        ? 'completed'
        : 'upcoming';
  return {
    ...match,
    matchId: score.match_id,
    status,
    score: tally(score),
    games: scoreGames(score).map((game) => ({
      p1: game.player_1_score,
      p2: game.player_2_score,
      ...(isCompletedGame(game) ? {} : { partial: true }),
    })),
    liveGame:
      status === 'live' && score.current_game
        ? { p1: score.current_game.player_1_score, p2: score.current_game.player_2_score }
        : null,
    isWalkover: score.is_walkover,
    outcome: score.outcome,
    winnerId: score.winner_id,
    retiredPlayerId: score.retired_player_id ?? null,
  };
}

export function applyDrawScores(
  draw: DrawResult,
  drawId: string,
  scores: Record<string, EventLiveScore>,
): DrawResult {
  const byCode = new Map(
    Object.values(scores)
      .filter((s) => s.draw_id === drawId)
      .map((s) => [s.draw_match_id, s]),
  );
  return {
    ...draw,
    stages: draw.stages.map((stage) => ({
      ...stage,
      rounds: stage.rounds.map((round) => ({
        ...round,
        matches: round.matches.map((match) => {
          const score =
            (match.eventMatchSchedule && scores[match.eventMatchSchedule.id]) ||
            byCode.get(match.matchId);
          if (!score || (score.draw_id !== null && score.draw_id !== drawId)) return match;
          const completed = score.status === 'completed';
          const winner =
            completed && score.winner_id
              ? [match.player1, match.player2].find((p) => p?.id === score.winner_id)
              : undefined;
          return {
            ...match,
            scorecardMatchId: score.match_id ?? match.scorecardMatchId,
            eventMatchSchedule: match.eventMatchSchedule
              ? { ...match.eventMatchSchedule, match_id: score.match_id, status: score.status }
              : match.eventMatchSchedule,
            status: (completed
              ? 'completed'
              : score.status === 'in_progress'
                ? 'in_progress'
                : 'pending') as 'completed' | 'in_progress' | 'pending',
            gamesWon:
              score.player_1_games === null || score.player_2_games === null
                ? null
                : { player1: score.player_1_games, player2: score.player_2_games },
            score: scoreGames(score).map((game) => ({
              game: game.game_number,
              player1Score: game.player_1_score,
              player2Score: game.player_2_score,
              ...(isCompletedGame(game) ? {} : { status: game.status }),
            })),
            winner,
            loser: winner
              ? [match.player1, match.player2].find((p) => p && p.id !== winner.id)
              : undefined,
            isWalkover: score.is_walkover,
            outcome: score.outcome,
          };
        }),
      })),
    })),
  };
}

export function isEventActiveOnDay(
  event: { start_date: string | null; end_date: string | null },
  today: string | null,
  zone: string,
): boolean {
  if (today === null) return false;
  const key = (value: string | null) =>
    value && Number.isFinite(Date.parse(value)) ? todayKeyInZone(Date.parse(value), zone) : null;
  const start = key(event.start_date);
  const end = key(event.end_date);
  return (start === null || today >= start) && (end === null || today <= end);
}
